import * as FileSaver from "file-saver";
import * as XLSX from "sheetjs-style";

const fileType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const fileExtension = ".xlsx";

const saveWorkbook = (wb, fileName) => {
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(blob, fileName + fileExtension);
};

// Excel export button.
// - Single sheet (default): pass `data` (array of row objects) + optional
//   `header` (string[] that overrides row 1).
// - Multiple sheets: pass `sheets` = [{ name, data, header? }] and it builds a
//   workbook with one tab per entry (data/header work the same as above).
// `getData` (optional): async () => rows. When provided (single-sheet only),
// the rows are fetched on click instead of taken from `data` — used for
// server-paginated tables whose full export isn't held in memory.
const Excel = ({
  fileName = "",
  data = [],
  button = <></>,
  disabled = false,
  header = [],
  sheets = null,
  getData = null,
}) => {
  const buildSheet = (sheetData = [], sheetHeader = []) => {
    const ws = XLSX.utils.json_to_sheet(sheetData);
    if (sheetHeader.length > 0) {
      XLSX.utils.sheet_add_aoa(ws, [sheetHeader], { origin: "A1" });
    }
    // Auto-fit column widths so labels/values aren't truncated (per column =
    // widest of its header + cells, padded a little, capped so it stays sane).
    const keys = sheetData.length > 0 ? Object.keys(sheetData[0]) : [];
    if (keys.length > 0) {
      ws["!cols"] = keys.map((key, i) => {
        let max = String(sheetHeader[i] ?? key).length;
        sheetData.forEach((row) => {
          const len = String(row[key] ?? "").length;
          if (len > max) max = len;
        });
        return { wch: Math.min(60, max + 2) };
      });
    }
    return ws;
  };

  const exportExcel = async () => {
    if (Array.isArray(sheets) && sheets.length > 0) {
      const Sheets = {};
      const SheetNames = [];
      sheets.forEach((s, i) => {
        // Excel tab names cap at 31 chars and can't contain []:*?/\
        const name = (s?.name || `Sheet${i + 1}`)
          .replace(/[[\]:*?/\\]/g, " ")
          .slice(0, 31);
        Sheets[name] = buildSheet(s?.data ?? [], s?.header ?? []);
        SheetNames.push(name);
      });
      saveWorkbook({ Sheets, SheetNames }, fileName);
      return;
    }
    // Single sheet: fetch the rows on click when a getData fn is supplied
    // (server-paginated source), otherwise use the in-memory `data`.
    const rows =
      typeof getData === "function" ? (await getData()) ?? [] : data;
    saveWorkbook(
      { Sheets: { Sheet1: buildSheet(rows, header) }, SheetNames: ["Sheet1"] },
      fileName
    );
  };

  return (
    <div onClick={disabled ? () => {} : () => exportExcel()}>{button}</div>
  );
};

export default Excel;
